import { Files, Plus } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useCreateDocumentFromTemplate,
  useDocuments,
  useEnsureStarterTemplates,
  usePromoteToTemplate,
  useTemplates,
} from "~/api/queries";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/**
 * W10 §9 — the real Templates section.
 *
 * What it was: a `useDocuments({ status: "published", limit: 5 })` list, i.e. five ordinary
 * documents. `documents.list` **excludes** templates unless asked (`template: true`), so the
 * old list could not have contained a single template — the two sets are disjoint on the wire.
 *
 * Two verbs, named for what the platform really does:
 * - **Promote** is `documents.templatize`, which MUTATES the document into a template. There is
 *   no un-templatize endpoint, so it is confirmed and says it is one-way.
 * - **Use** is `documents.create { templateId }` — one call; the server copies the template's
 *   content, text, icon and colour.
 */
export function Templates() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: templates, isLoading } = useTemplates();
  const { data: documents } = useDocuments({ status: "published", limit: 100 });
  const ensureStarters = useEnsureStarterTemplates();
  const promote = usePromoteToTemplate();
  const createFromTemplate = useCreateDocumentFromTemplate();
  const [promoting, setPromoting] = useState("");
  const [confirming, setConfirming] = useState(false);

  const templateIds = new Set((templates ?? []).map((template) => template.id));
  const promotable = (documents ?? []).filter((document) => !templateIds.has(document.id));
  const promotingTitle = promotable.find((document) => document.id === promoting)?.title ?? "";

  const use = (templateId: string) =>
    createFromTemplate.mutate(
      { templateId },
      {
        onSuccess: (document) => {
          toast.success(t("Created “{{title}}”", { title: document.title }));
          void navigate({ to: "/documents/$documentId", params: { documentId: document.id } });
        },
      },
    );

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Text variant="small">
        {t("A template is a document the workspace starts new documents from.")}
      </Text>

      {templates?.length ? (
        <ul className="flex flex-col rounded-md border border-divider">
          {templates.map((template) => (
            <li
              key={template.id}
              className="flex items-center gap-3 border-b border-divider px-3 py-2.5 last:border-b-0"
            >
              <span aria-hidden className="w-5 text-center">
                {template.emoji ?? "📄"}
              </span>
              <span className="min-w-0 flex-1 truncate text-body text-text">{template.title}</span>
              <Button
                size="sm"
                variant="neutral"
                disabled={createFromTemplate.isPending}
                onClick={() => use(template.id)}
              >
                {t("New from this")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  navigate({
                    to: "/documents/$documentId",
                    params: { documentId: template.id },
                  })
                }
              >
                {t("Edit")}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <Empty icon={Files} message={t("No templates yet")} />
      )}

      <div className="flex flex-col gap-2 border-t border-divider pt-3">
        <Text variant="small" className="uppercase tracking-wide">
          {t("Promote a document")}
        </Text>
        <div className="flex items-end gap-2">
          <Select
            label={t("Document")}
            value={promoting}
            onChange={(event) => setPromoting(event.target.value)}
          >
            <option value="">{t("Choose a document")}</option>
            {promotable.map((document) => (
              <option key={document.id} value={document.id}>
                {document.title}
              </option>
            ))}
          </Select>
          <Button
            variant="neutral"
            icon={Plus}
            disabled={!promoting || promote.isPending}
            onClick={() => setConfirming(true)}
          >
            {t("Make it a template")}
          </Button>
        </div>
        <Text variant="small" className="text-text-tertiary">
          {t("The document itself becomes the template — this cannot be undone.")}
        </Text>
      </div>

      {templates?.length === 0 ? (
        <div>
          <Button
            variant="ghost"
            disabled={ensureStarters.isPending}
            onClick={() =>
              ensureStarters.mutate(undefined, {
                onSuccess: (created) =>
                  toast.success(t("Added {{count}} starter templates", { count: created.length })),
              })
            }
          >
            {ensureStarters.isPending ? t("Adding…") : t("Add the starter templates")}
          </Button>
        </div>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setConfirming(false)}
          title={t("Make it a template")}
          description={t(
            "“{{title}}” becomes a template. It leaves the document list and there is no way to turn it back.",
            { title: promotingTitle },
          )}
          confirmLabel={t("Make it a template")}
          cancelLabel={t("Cancel")}
          closeLabel={t("Close")}
          dangerous
          busy={promote.isPending}
          onConfirm={() =>
            promote.mutate(
              { documentId: promoting },
              {
                onSuccess: () => {
                  setConfirming(false);
                  setPromoting("");
                  toast.success(t("It is a template now"));
                },
              },
            )
          }
        />
      ) : null}
    </div>
  );
}
